import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod/v4";
import { useNavigate, Link } from "react-router-dom";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useTheme } from "@mui/material/styles";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { enqueueSnackbar } from "notistack";

import { useTheme as useAppTheme } from "../../context/ThemeContext";
import { deleteAllCookies, getLoginSecured } from "../../utils/common";
import { setCrossDomainCookie } from "../../utils/cookie-utils";
import { getSHA256Hash } from "../../utils/api";
import envConfig from "../../config";
import { useTracking } from "../../hooks/useTracking";
import BackdropLoading from "../../components/BackdropLoading/BackdropLoading";
import AuthExaccLayout from "../../components/AuthExaccLayout/AuthExaccLayout";
import { formatPhoneDisplay } from "../../utils/formatPhone";
import HelpTooltip from "../../components/HelpTooltip/HelpTooltip";
import ThemeToggle from "../../components/ThemeToggle/ThemeToggle";

/** Suggestions for free-text "How did you hear about us?" */
const REFERRAL_OPTIONS = [
  "Google search",
  "Friend or colleague",
  "Social media",
  "LinkedIn",
  "YouTube",
  "Article or blog post",
  "Conference or event",
  "Accountant referral",
  "Other",
];

/** Append visitor/campaign tracker when present: "Google search|abc123abc" */
function buildReferralSource(text) {
  const trimmed = (text || "").trim();
  if (!trimmed) return "";
  const tracker =
    new URLSearchParams(window.location.search).get("tracker") ||
    localStorage.getItem("uuid");
  return tracker ? `${trimmed}|${tracker}` : trimmed;
}

const registerSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  nickName: z.string().optional(),
  email: z.string().min(1, "Email is required").email("Invalid email format"),
  mobileNumber: z.string().refine(
    (v) => {
      const d = v.replace(/\D/g, "");
      return d && (d.startsWith("4") || d.startsWith("04"));
    },
    "Invalid Australian Mobile Number"
  ),
  password: z
    .string()
    .min(10, "Password needs to be at least 10 characters, including 1 uppercase, 1 lowercase, 1 digit and 1 special character.")
    .regex(
      /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*\W)(?!.*\s).{10,}$/,
      "Password needs to be at least 10 characters, including 1 uppercase, 1 lowercase, 1 digit and 1 special character."
    ),
  referralCode: z.string().optional(),
  referralSource: z.string().optional(),
  termsAndConditions: z.boolean().refine((v) => v === true, "You must accept the Terms & Conditions"),
});

const PHONE_PREFIX = "+61";

function RegisterFormInner() {
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const { trackRegistrationStart, trackRegistrationSuccess, trackButtonClick } = useTracking();
  const { isDarkMode } = useAppTheme();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [showPassword, setShowPassword] = useState(false);
  const [isEmailDisabled, setIsEmailDisabled] = useState(false);
  const [loadingPrefill, setLoadingPrefill] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      nickName: "",
      email: "",
      mobileNumber: "",
      password: "",
      referralCode: new URLSearchParams(window.location.search).get("referralCode") || "",
      referralSource: "",
      termsAndConditions: false,
    },
  });

  const mobileRaw = watch("mobileNumber");

  useEffect(() => {
    trackRegistrationStart();
  }, []);

  useEffect(() => {
    const checkLoginEmail = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const loginEmail = urlParams.get("loginemail");
      if (!loginEmail) return;

      setLoadingPrefill(true);
      const url = `https://${envConfig.apiDev1}/api/v3/en-au/users/get-basic-details-by-hash-async?hash=${loginEmail}&BaseHostURL=s1v.exacc.au`;
      const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
        "olivs-api-real-ip": "31.42.0.186",
        "olivs-api-computer-name": "EC2AMAZ-QF1CQAD",
        "api-key": "OlivsAuth",
        "x-skip-auth-interceptor": "true",
      };

      try {
        const response = await fetch(url, { method: "POST", headers });
        const responseData = await response.json();
        if (responseData.GlobalUserDetails) {
          const userData = responseData.GlobalUserDetails;
          let mobileNumber = "";
          if (userData.xxuPhone) {
            mobileNumber = userData.xxuPhone.startsWith("+61")
              ? userData.xxuPhone.substring(3)
              : userData.xxuPhone;
          }
          setValue("firstName", userData.xxuFirstName || "");
          setValue("lastName", userData.xxuLastName || "");
          setValue("nickName", userData.xxuNickName || "");
          setValue("email", userData.xxuLoginEmail || "");
          setValue("mobileNumber", mobileNumber.replace(/\D/g, ""));
          if (userData.xxuLoginEmail) setIsEmailDisabled(true);
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
      } finally {
        setLoadingPrefill(false);
      }
    };

    checkLoginEmail();
  }, [setValue]);

  const onSubmit = async (values) => {
    if (!executeRecaptcha) return;
    let recaptchaToken;
    try {
      recaptchaToken = await executeRecaptcha("register");
    } catch {
      enqueueSnackbar("reCAPTCHA verification failed. Please try again.", { variant: "error" });
      return;
    }

    const url = `https://${envConfig.apiDev1}/api/v5/en-au/users/create-new?sendemailYN=N&BaseHostURL=${envConfig.loginUrl}`;
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
      "olivs-api-real-ip": "31.42.0.186",
      "olivs-api-computer-name": "EC2AMAZ-QF1CQAD",
      "api-key": "OlivsAuth",
      captchav3: envConfig.loginUrl + ":" + recaptchaToken,
    };

    let mobileNumber = values.mobileNumber.replace(/\D/g, "");
    if (mobileNumber.startsWith("0")) {
      mobileNumber = mobileNumber.slice(1);
    }

    const body = JSON.stringify({
      FirstName: values.firstName,
      LastName: values.lastName,
      NickName: values.nickName ?? "",
      LoginEmail: values.email,
      MobilePhoneNo: PHONE_PREFIX + mobileNumber,
      Password: values.password,
      Sha2Hash: getSHA256Hash(values.password),
      xxuRefCode: values.referralCode || undefined,
      ReferralSource: buildReferralSource(values.referralSource),
    });

    try {
      const response = await fetch(url, { method: "POST", headers, body });
      deleteAllCookies();
      const responseData = await response.json();
      if (responseData.SuccessYN === "Y") {
        trackRegistrationSuccess(values.email);
        const loginSecured = await getLoginSecured(values.email, values.password);
        setCrossDomainCookie("loginEmail", values.email);
        setCrossDomainCookie("xxuStatus", "E");
        setCrossDomainCookie("emailVerified", "N");
        navigate("/verify-email", {
          state: {
            firstName: values.firstName,
            email: values.email,
            loginSecured,
            phone: PHONE_PREFIX + mobileNumber,
          },
        });
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  return (
    <>
      <BackdropLoading open={loadingPrefill} />
      <Box
        className="glass-card"
        sx={{ p: { xs: 3.5, md: 4 }, width: "100%", maxWidth: 460, mx: "auto", position: "relative" }}
      >
        <Typography variant="h2" sx={{ fontWeight: 700, mb: 1, color: "text.primary", mt: 0 }}>
          Create Your Account
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 4 }}>
          Free onboarding. No credit card required.
        </Typography>

        <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 1 }}>
          <input type="hidden" {...register("referralCode")} />
          <input type="hidden" {...register("nickName")} />

          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2, mb: 2.5 }}>
            <TextField
              label="First Name"
              required
              fullWidth
              {...register("firstName")}
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
              data-testid="register-panel--TextField-0"
            />
            <TextField
              label="Last Name"
              required
              fullWidth
              {...register("lastName")}
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
              data-testid="register-panel--TextField-1"
            />
          </Box>

          <TextField
            label="Email Address"
            type="email"
            required
            fullWidth
            disabled={isEmailDisabled}
            {...register("email")}
            error={!!errors.email}
            helperText={errors.email?.message}
            inputProps={{ autoComplete: "off" }}
            sx={{ mb: 2.5 }}
            data-testid="register-panel--TextField-5"
          />

          <Box sx={{ display: "flex", gap: 1.5, mb: 2.5, alignItems: "flex-start" }}>
            <Box sx={{ display: "flex", gap: 2, flex: 1, alignItems: "flex-start", minWidth: 0 }}>
              <TextField value="+61" disabled sx={{ width: 84, flexShrink: 0 }} data-testid="register-panel--TextField-3" />
              <TextField
                label="Australian Mobile Phone"
                type="tel"
                required
                fullWidth
                value={formatPhoneDisplay(mobileRaw, true)}
                onChange={(e) => {
                  const rawDigits = e.target.value.replace(/\D/g, "");
                  setValue("mobileNumber", rawDigits, { shouldValidate: true });
                }}
                onBlur={() => setValue("mobileNumber", mobileRaw, { shouldValidate: true })}
                error={!!errors.mobileNumber}
                helperText={errors.mobileNumber?.message}
                data-testid="register-panel--TextField-4"
              />
            </Box>
            <Box sx={{ flexShrink: 0, pt: 1 }}>
              <HelpTooltip tooltipContent="Australian Mobile Phone is required for account verification and recovery." />
            </Box>
          </Box>

          <TextField
            label="Password"
            type={showPassword ? "text" : "password"}
            required
            fullWidth
            {...register("password")}
            error={!!errors.password}
            helperText={errors.password?.message}
            inputProps={{ autoComplete: "off" }}
            sx={{ mb: 2.5 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                    sx={{ color: "text.secondary" }}
                    data-testid="register-panel--toggle-password"
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            data-testid="register-panel--TextField-6"
          />

          <Controller
            name="referralSource"
            control={control}
            render={({ field: { onChange, value, ...field } }) => (
              <Autocomplete
                {...field}
                freeSolo
                options={REFERRAL_OPTIONS}
                value={value || ""}
                onChange={(_, newValue) => onChange(newValue || "")}
                onInputChange={(_, newInputValue, reason) => {
                  if (reason === "input" || reason === "clear") {
                    onChange(newInputValue);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="How did you hear about us?"
                    fullWidth
                    sx={{ mb: 3 }}
                  />
                )}
                componentsProps={{
                  paper: {
                    sx: {
                      bgcolor: "background.paper",
                      backgroundImage: "none",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1.5,
                      mt: 0.5,
                      "& .MuiAutocomplete-option": { fontSize: 14 },
                    },
                  },
                }}
              />
            )}
          />

          <Box sx={{ mb: 3 }}>
            <Controller
              name="termsAndConditions"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox {...field} checked={field.value} data-testid="register-panel--Checkbox-0" />}
                  label={
                    <Typography variant="body2" color="text.secondary" component="span">
                      I agree to Exacc{" "}
                      <Box
                        component="a"
                        href="https://exacc.au/terms-of-use"
                        onClick={(e) => {
                          e.preventDefault();
                          trackButtonClick("Terms and Conditions");
                          window.open("https://exacc.au/terms-of-use", "_blank");
                        }}
                        sx={{
                          color: "primary.main",
                          cursor: "pointer",
                          fontWeight: 700,
                          textDecoration: "none",
                          "&:hover": { textDecoration: "underline" },
                        }}
                        data-testid="register-panel--Link-0"
                      >
                        Terms &amp; Conditions
                      </Box>{" "}
                      and{" "}
                      <MuiLink
                        href="https://exacc.au/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        color="primary"
                        underline="hover"
                        data-testid="register-panel--Link-1"
                      >
                        Privacy Policy
                      </MuiLink>
                      <Box component="span" sx={{ color: muiTheme.palette.error.main }}>
                        {" "}
                        *
                      </Box>
                    </Typography>
                  }
                  data-testid="register-panel--FormControlLabel-0"
                />
              )}
            />
            {errors.termsAndConditions && (
              <Typography variant="body2" color="error" sx={{ mt: 0.5, ml: 4 }}>
                {errors.termsAndConditions.message}
              </Typography>
            )}
          </Box>

          <Button
            type="submit"
            variant="outlined"
            className="btn-glass"
            fullWidth
            disabled={isSubmitting}
            sx={{ mb: 3 }}
            data-testid="register-panel--Button-0"
          >
            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Sign Up"}
          </Button>

          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?
            </Typography>
            <Button
              component={Link}
              to="/login"
              variant="text"
              sx={{
                p: 0,
                minWidth: 0,
                fontWeight: 600,
                fontSize: 14,
                color: "primary.main",
                "&:hover": { background: "transparent", color: "primary.dark" },
              }}
              data-testid="register-panel--Link-2"
            >
              Login Now
            </Button>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <ThemeToggle title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"} />
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default function RegisterPage() {
  return (
    <AuthExaccLayout>
      <RegisterFormInner />
    </AuthExaccLayout>
  );
}
